import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Clock, Users, BookOpen, ChevronRight, AlertCircle } from 'lucide-react';
import { StoryChapter, Choice, StoryProgress } from '../types/dualStory';
import { getStoryChapters, saveProgress, markStoryCompleted } from '../services/dualStoryService';
import './DualStoryPage.css';

// 对话内容
const dialogueContent = [
  {
    speaker: "旁白",
    content: "夜深了，梧桐派出所的灯光依然明亮。陆鸣刚刚整理完双线的案件卷宗，韩师傅端着两杯热茶走了过来。"
  },
  {
    speaker: "老韩",
    content: "陆鸣，辛苦了。看完这两个案子，有什么感悟吗？"
  },
  {
    speaker: "陆鸣",
    content: "韩师傅，我发现...虽然时代不同，手法各异，但骗子利用的都是人性的弱点。贪婪、恐惧、孤独...这些从来没有变过。"
  },
  {
    speaker: "老韩",
    content: "（欣慰地笑了笑）你说到点子上了。从古至今，变的只是工具，不变的是人心。"
  },
  {
    speaker: "老韩",
    content: "古代的炼丹术，现代的电信诈骗，本质上都是利用人们想要'走捷径'的心理。"
  },
  {
    speaker: "陆鸣",
    content: "是的，而且我发现，最容易上当的往往是那些最善良、最信任他人的人。"
  },
  {
    speaker: "老韩",
    content: "所以我们的工作，不仅仅是抓坏人，更重要的是守护好这些善良的人。"
  },
  {
    speaker: "老韩",
    content: "来，我为你准备了一个特别的思考题。它考验的不是破案技巧，而是对人性的理解。"
  },
  {
    speaker: "系统提示",
    content: "🌟 恭喜！你已解锁核心启示题目：'古今人性的永恒弱点'"
  }
];

// 组件状态

const DualStoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // 状态管理
  const [currentChapter, setCurrentChapter] = useState<StoryChapter | null>(null);
  const [chapterHistory, setChapterHistory] = useState<string[]>([]);
  const [showChoices, setShowChoices] = useState(true);
  const [timeline, setTimeline] = useState<'modern' | 'ancient' | 'final'>('modern');
  const [loading, setLoading] = useState(true);
  const [unlockedClues, setUnlockedClues] = useState<string[]>([]);
  const [showTimelineSwitch, setShowTimelineSwitch] = useState(false);
  const [allChapters, setAllChapters] = useState<StoryChapter[]>([]);
  const [showDialogue, setShowDialogue] = useState(false);
  const [dialogueStep, setDialogueStep] = useState(0);
  const [branchTip, setBranchTip] = useState('');

  useEffect(() => {
    initializeStory();
  }, [id]);

  const initializeStory = async () => {
    if (!id) return;

    setLoading(true);
    try {
      // 从服务加载章节
      const chapters = await getStoryChapters(id);
      setAllChapters(chapters);

      if (chapters.length > 0) {
        setCurrentChapter(chapters[0]);
        setChapterHistory([chapters[0].id]);
        setTimeline(chapters[0].timeline);
      }
    } catch (error) {
      console.error('加载故事失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChoice = async (choice: Choice) => {
    // 先查找下一章
    const nextChapter = allChapters.find((ch: StoryChapter) => ch.id === choice.nextChapter);

    // 死链防护：目标章节不存在时提示且不推进（不计通关、不保存进度）
    if (!nextChapter) {
      setBranchTip('该支线剧情尚在创作中，请选择其他选项');
      return;
    }
    setBranchTip('');

    // 保存进度
    const progress: StoryProgress = {
      currentChapter: choice.nextChapter,
      chapterHistory: [...chapterHistory, choice.nextChapter],
      unlockedClues,
      choices: [...(currentChapter?.choices?.map(c => c.id) || []), choice.id],
      timeline
    };
    if (id) saveProgress(id, progress);

    setCurrentChapter(nextChapter);
    setChapterHistory([...chapterHistory, nextChapter.id]);
    setShowChoices(false);

    // 延迟显示选择
    setTimeout(() => {
      setShowChoices(true);
    }, 500);

    // 只有抵达真正的结局章（无后续选择）才记为通关
    if (!nextChapter.choices || nextChapter.choices.length === 0) {
      handleStoryComplete();
    }
  };

  const handleTimelineSwitch = async () => {
    // 切换到另一个时线
    const newTimeline = timeline === 'modern' ? 'ancient' : 'modern';
    setTimeline(newTimeline);

    // 加载对应时线的第一章
    const timelineId = newTimeline === 'modern' ? '1' : '2';
    try {
      const chapters = await getStoryChapters(timelineId);
      setAllChapters(chapters);

      if (chapters.length > 0) {
        setCurrentChapter(chapters[0]);
        setChapterHistory([chapters[0].id]);
        setShowChoices(true);
      }
    } catch (error) {
      console.error('切换时线失败:', error);
    }
  };

  const handleStoryComplete = async () => {
    if (id) {
      // 标记故事完成
      markStoryCompleted(id);

      // 保存故事详细进度，包括完成状态
      const storyProgress = {
        currentChapter: currentChapter?.id || '',
        completed: true,
        unlockedClues: unlockedClues,
        timeline: timeline,
        completedAt: new Date().toISOString()
      };
      saveProgress(id, storyProgress);

      // 检查是否完成了双线叙事
      const savedProgress = localStorage.getItem('dualStoryProgress') || '{}';
      const progress = JSON.parse(savedProgress);

      // 保存双线叙事完成状态
      const completedStories = progress.completedStories || [];
      if (!completedStories.includes(id)) {
        completedStories.push(id);
      }

      localStorage.setItem('dualStoryProgress', JSON.stringify({
        ...progress,
        completedStories,
        dualStoryCompleted: completedStories.length >= 2
      }));

      // 只有完成两条线后才显示对话剧情
      if (completedStories.length >= 2) {
        setShowDialogue(true);
      } else {
        // 如果只完成一条线，显示提示
        alert(`您已完成${timeline === 'modern' ? '现代线' : '古代线'}，请继续体验另一条时间线的故事！`);
        navigate('/dual-story');
      }
    }
  };

  const handleDialogueNext = () => {
    if (dialogueStep < dialogueContent.length - 1) {
      setDialogueStep(dialogueStep + 1);
    } else {
      // 对话结束，解锁核心启示题目
      unlockInsightStory();
    }
  };

  const unlockInsightStory = () => {
    // 解锁核心启示题目
    const savedProgress = localStorage.getItem('gameProgress') || '{}';
    const gameProgress = JSON.parse(savedProgress);

    if (!gameProgress.unlockedStories) {
      gameProgress.unlockedStories = [];
    }

    if (!gameProgress.unlockedStories.includes('core_insight')) {
      gameProgress.unlockedStories.push('core_insight');
      // 设置双线剧情完成标记
      gameProgress.dualStoryCompleted = true;
      localStorage.setItem('gameProgress', JSON.stringify(gameProgress));
    }

    // 导航到游戏页面
    navigate('/game?insight=unlocked');
  };

  const handleBack = () => {
    if (chapterHistory.length > 1) {
      // 返回上一章
      const prevChapterId = chapterHistory[chapterHistory.length - 2];
      const prevChapter = allChapters.find((ch: StoryChapter) => ch.id === prevChapterId);

      if (prevChapter) {
        setCurrentChapter(prevChapter);
        setChapterHistory(chapterHistory.slice(0, -1));
      }
    } else {
      // 返回列表
      navigate('/dual-story');
    }
  };

  // 渲染对话剧情
  if (showDialogue) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
        {/* 顶部导航 */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex justify-between items-center max-w-4xl mx-auto">
            <button
              onClick={() => navigate('/dual-story')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              返回列表
            </button>
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-gray-600">
                <Users className="w-4 h-4 mr-1" />
                师徒对话
              </div>
            </div>
          </div>
        </header>

        {/* 对话内容 */}
        <main className="max-w-4xl mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-xl p-8"
          >
            {/* 场景描述 */}
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">师徒夜话</h2>
              <p className="text-gray-600">双线叙事后的深刻对话</p>
            </div>

            {/* 对话内容 */}
            <div className="mb-8">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                <div className="flex items-start mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                    dialogueContent[dialogueStep].speaker === '老韩' ? 'bg-ocean-600' :
                    dialogueContent[dialogueStep].speaker === '陆鸣' ? 'bg-warm-600' :
                    'bg-gray-600'
                  }`}>
                    <span className="text-white font-bold">
                      {dialogueContent[dialogueStep].speaker === '老韩' ? '韩' :
                       dialogueContent[dialogueStep].speaker === '陆鸣' ? '陆' :
                       '旁'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800 mb-2">
                      {dialogueContent[dialogueStep].speaker}
                    </h3>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      {dialogueContent[dialogueStep].content}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 进度指示 */}
            <div className="flex justify-center mb-8">
              <div className="flex space-x-2">
                {dialogueContent.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index <= dialogueStep ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex justify-center">
              {dialogueStep === dialogueContent.length - 1 ? (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={unlockInsightStory}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg font-medium"
                >
                  开始挑战核心启示题目
                  <ChevronRight className="inline-block w-5 h-5 ml-2" />
                </motion.button>
              ) : (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDialogueNext}
                  className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-lg font-medium"
                >
                  继续
                  <ChevronRight className="inline-block w-5 h-5 ml-2" />
                </motion.button>
              )}
            </div>

            {/* 底部提示 */}
            {dialogueStep < dialogueContent.length - 1 && (
              <div className="mt-8 text-center text-sm text-gray-500">
                <p>💡 师徒的对话将揭示更深层的真相</p>
              </div>
            )}
          </motion.div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">加载剧情中...</p>
        </div>
      </div>
    );
  }

  if (!currentChapter) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 text-xl">剧情不存在</p>
          <button
            onClick={() => navigate('/dual-story')}
            className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            返回故事列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      {/* 顶部导航 */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="flex justify-between items-center max-w-4xl mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            {chapterHistory.length > 1 ? '返回上一步' : '返回列表'}
          </button>

          <div className="flex items-center space-x-4">
            <div className="flex items-center text-sm text-gray-600">
              <Clock className="w-4 h-4 mr-1" />
              第 {chapterHistory.length} 章
            </div>

            {/* 时线标签 */}
            <div className={`px-3 py-1 rounded-full text-sm font-medium text-white ${
              timeline === 'modern'
                ? 'bg-gradient-to-r from-blue-500 to-blue-700'
                : timeline === 'ancient'
                ? 'bg-gradient-to-r from-amber-500 to-amber-700'
                : 'bg-gradient-to-r from-purple-500 to-purple-700'
            }`}>
              {timeline === 'modern' ? '现代线' : timeline === 'ancient' ? '古代线' : '新的篇章'}
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容区 */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-xl p-8"
        >
          {/* 章节标题 */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {currentChapter.title}
            </h2>
            {currentChapter.speaker && (
              <p className="text-sm text-gray-600 italic">
                — {currentChapter.speaker}
              </p>
            )}
          </div>

          {/* 章节内容 */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentChapter.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="mb-8"
            >
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed text-lg">
                  {currentChapter.content}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* 选择按钮 */}
          <AnimatePresence>
            {showChoices && currentChapter.choices && currentChapter.choices.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-3"
              >
                {branchTip && (
                  <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                    {branchTip}
                  </div>
                )}
                {currentChapter.choices.map((choice) => {
                  // 指向尚未创作章节的选项：禁用置灰并标注，避免玩家撞入死链
                  const isBranchLocked = !allChapters.some(ch => ch.id === choice.nextChapter);
                  return (
                  <motion.button
                    key={choice.id}
                    whileHover={{ scale: isBranchLocked ? 1 : 1.02 }}
                    whileTap={{ scale: isBranchLocked ? 1 : 0.98 }}
                    onClick={() => !isBranchLocked && handleChoice(choice)}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-200 border group ${
                      isBranchLocked
                        ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 border-purple-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${isBranchLocked ? 'text-gray-400' : 'text-gray-800'}`}>
                        {choice.text}
                        {isBranchLocked && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500 align-middle">
                            创作中
                          </span>
                        )}
                      </span>
                      {!isBranchLocked && (
                        <ChevronRight className="w-5 h-5 text-purple-600 group-hover:translate-x-1 transition-transform" />
                      )}
                    </div>
                  </motion.button>
                  );
                })}
              </motion.div>
            ) : (
              /* 结局页面 */
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 pt-8 border-t border-gray-200"
              >
                <div className="text-center space-y-6">
                  {/* 返回菜单按钮 */}
                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={() => navigate('/dual-story')}
                      className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 transition-all duration-200 shadow-lg"
                    >
                      返回菜单
                    </button>
                    <button
                      onClick={() => navigate('/')}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg"
                    >
                      返回主页
                    </button>
                  </div>

                  {/* 顿悟时刻提示 - 只在故事完成时显示 */}
                  {timeline === 'final' && currentChapter.choices && currentChapter.choices.length === 0 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg"
                    >
                      <div className="flex items-center justify-center mb-4">
                        <span className="text-4xl">💡</span>
                      </div>
                      <h3 className="text-lg font-bold text-purple-800 mb-3">
                        故事已完成，准备接受新的挑战！
                      </h3>
                      <p className="text-gray-700 mb-4">
                        现在去体验顿悟时刻的海龟汤推理，测试你对这个故事的理解！
                      </p>
                      <button
                        onClick={() => {
                          navigate('/game');
                          // 延迟一下确保页面加载完成后再切换标签
                          setTimeout(() => {
                            const insightButton = Array.from(document.querySelectorAll('button')).find(btn => btn.textContent?.includes('顿悟时刻'));
                            if (insightButton) {
                              insightButton.click();
                            }
                          }, 100);
                        }}
                        className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-lg font-medium"
                      >
                        前往顿悟时刻 →
                      </button>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* 时线切换按钮（在特定章节后显示） */}
          {chapterHistory.length >= 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-8 pt-8 border-t border-gray-200"
            >
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  想要了解另一条时间线的故事吗？
                </p>
                <button
                  onClick={handleTimelineSwitch}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-lg"
                >
                  切换到{timeline === 'modern' ? '古代' : '现代'}线
                </button>
              </div>
            </motion.div>
          )}

          {/* 已解锁的线索 */}
          {unlockedClues.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg"
            >
              <h4 className="font-bold text-amber-800 mb-2 flex items-center">
                <BookOpen className="w-5 h-5 mr-2" />
                发现的线索
              </h4>
              <ul className="space-y-1 text-amber-700">
                {unlockedClues.map((clue, index) => (
                  <li key={index}>• {clue}</li>
                ))}
              </ul>
            </motion.div>
          )}
        </motion.div>

        {/* 底部提示 */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>💡 提示：你的选择将影响故事走向</p>
          {timeline === 'modern' && (
            <p className="mt-1">现代线与古代线相互关联，寻找其中的联系</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default DualStoryPage;